// All backend calls for the academy onboarding wizard. Each wrapper mirrors an
// existing page exactly (endpoints, id sources, payload shapes) — see the
// "source page" note above each section. Pure mappers are exported for tests.
import { fetchGet, fetchPut } from "app/utils/request";
import { countryCodes } from "app/utils/CountryCodes";
import { states } from "app/components/StripeForm/states";
import {
  schedulingFetchGet,
  schedulingFetchPatch,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";

// "Central Golf Club" -> "CG": first letters of the first two words, the glyph
// the wizard's cover-photo component shows in the avatar slot when no image is
// uploaded. Shared by the Facility and Coach Profile steps.
export const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

// ---------------------------------------------------------------------------
// General step — source page: /manage_facility (src/routes/Facility/index.tsx,
// EditFacility modal). Core API v1; the record exists for every academy coach.
// ---------------------------------------------------------------------------

export interface ICoreFacilityV1 {
  id: string;
  name: string;
  country: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  timezone: string;
}

export interface IGeneralForm {
  facilityName: string;
  country: string;
  usState: string;
  address: string;
  city: string;
  zip: string;
}

// The step's CountrySelector only offers `countryCodes`; a code outside that
// list (the BE has been seen holding "RE") would render as an empty
// "Select Country" while still being submitted unchanged. Treat it like a
// missing country so the coach sees, and can correct, a real selection.
const knownCountry = (code: string | null | undefined): string =>
  code && countryCodes.some((country) => country.code === code) ? code : "US";

// Same guard for the State select, which only offers `states` by
// abbreviation. The BE field is free text written by several flows: the
// legacy Edit Facility modal stores country-state-city iso codes (which add
// rows the list has no entry for, e.g. "UM-81"), and older records hold full
// names. A value the select cannot show rendered as "Select state" while
// still passing validateState as non-empty, so Next accepted a State the coach
// could not see. Names resolve to their abbreviation; anything else reads as
// unselected so the required-field error fires.
const knownState = (value: string | null | undefined): string => {
  if (!value) {
    return "";
  }
  const needle = value.trim().toLowerCase();
  const match = states.find(
    (state: { name: string; abbreviation: string }) =>
      state.abbreviation.toLowerCase() === needle ||
      state.name.toLowerCase() === needle,
  );
  return match ? match.abbreviation : "";
};

export const parseGeneralFacility = (
  facility: ICoreFacilityV1,
): IGeneralForm => ({
  facilityName: facility.name || "",
  country: knownCountry(facility.country),
  usState: knownState(facility.state),
  address: facility.street1 || "",
  city: facility.city || "",
  zip: facility.zip || "",
});

export const buildGeneralFacilityPayload = (
  facility: ICoreFacilityV1,
  form: IGeneralForm,
): ICoreFacilityV1 => ({
  ...facility,
  name: form.facilityName.trim(),
  country: form.country,
  // Non-US countries have no State field in the wizard; clear any stale value.
  state: form.country === "US" ? form.usState : "",
  street1: form.address.trim(),
  city: form.city.trim(),
  zip: form.zip.trim(),
});

// GET /facilities lists every facility the coach belongs to. The rest of
// Academy setup — /manage_facility's edits and the checklist's
// facility_profile flag — is keyed to the coach's scheduling facility
// (state.user.self.scheduling.facility), so the wizard edits that record. The
// first element is only the fallback for an account with no scheduling
// facility; a scheduling facility the list lacks leaves nothing to edit —
// saving into an unrelated facility would overwrite someone else's record.
export const pickGeneralFacility = (
  facilities: ICoreFacilityV1[] | null | undefined,
  currentFacilityId?: string | null,
): ICoreFacilityV1 | undefined => {
  if (!facilities || facilities.length === 0) {
    return undefined;
  }
  if (currentFacilityId == null) {
    return facilities[0];
  }
  return facilities.find((facility) => facility.id === currentFacilityId);
};

export const getGeneralFacility = (
  currentFacilityId?: string | null,
): Promise<ICoreFacilityV1 | undefined> =>
  fetchGet("/facilities", {}, { version: 1 }).then(
    (facilities: ICoreFacilityV1[]) =>
      pickGeneralFacility(facilities, currentFacilityId),
  );

export const saveGeneralFacility = (
  facility: ICoreFacilityV1,
  form: IGeneralForm,
): Promise<unknown> =>
  fetchPut(
    `/facilities/${facility.id}`,
    { facility: buildGeneralFacilityPayload(facility, form) },
    { version: 1 },
  );

// ---------------------------------------------------------------------------
// Facility step — source page: /manage_facility/landing_page
// (src/routes/LandingPage/index.tsx). Core API v2 + scheduling API, both keyed
// by the scheduling facility id from redux (state.user.self.scheduling.facility).
// Unlike the source page, public_landing_page_enabled is never sent — the
// wizard must not publish a half-finished page.
// ---------------------------------------------------------------------------

export interface ILandingFacility {
  id: string;
  name: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  description: string;
}

export interface ILandingForm {
  about: string;
  phone: string;
  email: string;
  website: string;
}

export const parseLandingFacility = (response: any): ILandingFacility => ({
  id: response.id,
  name: response.name || "",
  country: response.country || "US",
  phone: response.phone || "",
  email: response.email || "",
  website: response.website || "",
  description: response.description || "",
});

export const buildLandingFacilityPayload = (
  fetched: ILandingFacility,
  form: ILandingForm,
): ILandingFacility => ({
  ...fetched,
  description: form.about,
  phone: form.phone,
  email: form.email,
  website: form.website,
});

export const getLandingFacility = (
  facilityId: string,
): Promise<ILandingFacility> =>
  fetchGet(`facilities/${facilityId}`).then(parseLandingFacility);

export const saveLandingFacility = (
  fetched: ILandingFacility,
  form: ILandingForm,
): Promise<unknown> =>
  fetchPut(`facilities/${fetched.id}`, {
    facility: buildLandingFacilityPayload(fetched, form),
  });

export type WizardPaymentMethod = "in_person" | "stripe" | "both";

const PAYMENT_METHOD_STORAGE_KEY = "academy-wizard-payment-method";

export const loadPaymentMethod = (): WizardPaymentMethod => {
  const stored = sessionStorage.getItem(PAYMENT_METHOD_STORAGE_KEY);
  if (stored === "in_person" || stored === "stripe" || stored === "both") {
    return stored;
  }
  return "in_person";
};

export const savePaymentMethod = (method: WizardPaymentMethod): void => {
  sessionStorage.setItem(PAYMENT_METHOD_STORAGE_KEY, method);
};

export const paymentMethodToCollect = (
  method: WizardPaymentMethod,
): { collect_type: "offline" | "online" | "both"; online_default: boolean } => {
  if (method === "stripe") {
    return { collect_type: "online", online_default: true };
  }
  if (method === "both") {
    return { collect_type: "both", online_default: false };
  }
  return { collect_type: "offline", online_default: false };
};

// ---------------------------------------------------------------------------
// Booking Options step — source page: /calendar/pricing
// (src/routes/Scheduling/Pricing.tsx). Scheduling API. The whole lessonRates
// list is PATCHed back each time: rates without an id are created, rates with
// _destroy: true are removed. userId = state.user.self.id.
// ---------------------------------------------------------------------------

export interface ILessonRate {
  id?: string | number;
  title: string;
  duration: string;
  rate: number | string;
  location?: string;
  capacity?: number | string;
  defaultRate?: boolean;
  active?: boolean;
  _destroy?: boolean;
  timeTables?: Array<{ id: number }>;
  timeTableIds?: number[];
  [key: string]: any;
}

export interface IPricingGroup {
  id: string | number;
  title: string;
  lessonRates: ILessonRate[];
}

export interface IWizardBookingCard {
  id: number;
  serverId?: string | number;
  sessionName: string;
  duration: string;
  rate: string;
}

export const getPricingGroup = (
  userId: string,
): Promise<IPricingGroup | undefined> =>
  schedulingFetchGet(`coach_profiles/${userId}/pricing_groups`).then(
    (data: IPricingGroup[]) => data?.[0],
  );

export const parsePricingGroup = (
  group: IPricingGroup | undefined,
): IWizardBookingCard[] => {
  if (!group || !Array.isArray(group.lessonRates)) {
    return [];
  }
  return group.lessonRates
    .filter((rate) => !rate._destroy)
    .map((rate, index) => ({
      id: index,
      serverId: rate.id,
      sessionName: rate.title || "",
      duration: rate.duration || "60 mins",
      rate: rate.rate != null ? String(rate.rate) : "",
    }));
};

export const buildLessonRates = (
  group: IPricingGroup,
  cards: IWizardBookingCard[],
  method: WizardPaymentMethod,
): ILessonRate[] => {
  const payment = paymentMethodToCollect(method);
  const existingRates = Array.isArray(group.lessonRates)
    ? group.lessonRates
    : [];

  const kept = cards.map((card, index) => {
    const existing = existingRates.find(
      (rate) => card.serverId != null && rate.id === card.serverId,
    );
    return {
      ...(existing || {}),
      title: card.sessionName.trim(),
      duration: card.duration,
      rate: card.rate !== "" ? Number(card.rate) : 0,
      location: existing?.location ?? "",
      capacity: existing?.capacity ?? 1,
      // "The first one becomes your default" — the step's own subtitle.
      defaultRate: index === 0,
      active: existing?.active ?? true,
      // As on the Pricing page: the explicit ids first, the embedded tables
      // as the fallback, so an update never detaches a rate from its
      // schedules when the response carries only one of the two.
      timeTableIds:
        existing?.timeTableIds ??
        existing?.timeTables?.map((tt) => tt.id) ??
        [],
      ...payment,
    };
  });

  const keptServerIds = cards
    .map((card) => card.serverId)
    .filter((id) => id != null);
  // Rates already flagged _destroy were deleted by an earlier PATCH and only
  // linger in stale client state — never resend them.
  const removed = existingRates
    .filter(
      (rate) =>
        !rate._destroy && rate.id != null && !keptServerIds.includes(rate.id),
    )
    .map((rate) => ({
      ...rate,
      defaultRate: false,
      _destroy: true,
      timeTableIds:
        rate.timeTableIds ?? rate.timeTables?.map((tt) => tt.id) ?? [],
    }));

  return [...kept, ...removed];
};

export const savePricingGroup = (
  userId: string,
  group: IPricingGroup,
  cards: IWizardBookingCard[],
  method: WizardPaymentMethod,
): Promise<unknown> =>
  schedulingFetchPatch(`coach_profiles/${userId}/pricing_groups/${group.id}`, {
    ...group,
    lessonRates: buildLessonRates(group, cards, method),
  });

// ---------------------------------------------------------------------------
// Availability step — source page: /calendar/availability
// (src/routes/Scheduling/Availability.tsx). Scheduling API. The wizard owns at
// most one schedule; the mount GET decides POST (none yet) vs bulk_update.
// customPeriod: false → startDate/endDate are null (see utils.dateToString).
// ---------------------------------------------------------------------------

export const WIZARD_SCHEDULE_NAME = "General Availability";

export interface IDayAvailability {
  active: boolean;
  startTime: string;
  endTime: string;
}

export interface IWizardTimeTable {
  id?: string | number;
  name: string;
  // Write payloads carry startTime/endTime "HH:MM" strings; GET responses come
  // back with beginAt/endAt minutes since midnight instead (480 = "08:00") —
  // the same asymmetry /calendar/availability bridges with utils.convertTime.
  dailyTimeTables: Array<{
    day: number;
    active: boolean;
    startTime?: string;
    endTime?: string;
    beginAt?: number;
    endAt?: number;
  }>;
  customPeriod: boolean;
  startDate: string | null;
  endDate: string | null;
  [key: string]: any;
}

// Scheduling/utils.convertTime and parseTime, mirrored rather than imported:
// utils.ts pulls in app/i18n at module scope, which the api unit tests cannot
// load.
const minutesToTime = (minutes: number): string => {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const timeToMinutes = (time: string): number => {
  const [hour, minute] = time.split(":");
  return parseInt(hour, 10) * 60 + parseInt(minute, 10);
};

// The same hours /calendar/availability seeds for untouched days. begin_at is
// backend-validated, so a day with no time set cannot go up empty.
const DEFAULT_DAY_START = "08:00";
const DEFAULT_DAY_END = "17:00";

// The backend consumes begin_at/end_at minutes — AvailabilityWithDates does
// this same parseTime conversion before every save; the startTime/endTime
// strings ride along only for parity with /calendar/availability's payloads.
export const buildDailyTimeTables = (
  days: IDayAvailability[],
): IWizardTimeTable["dailyTimeTables"] =>
  days.map((entry, day) => {
    const startTime = entry.startTime || DEFAULT_DAY_START;
    const endTime = entry.endTime || DEFAULT_DAY_END;
    return {
      day,
      active: entry.active,
      beginAt: timeToMinutes(startTime),
      endAt: timeToMinutes(endTime),
      startTime,
      endTime,
    };
  });

export const parseDailyTimeTables = (
  timeTable: IWizardTimeTable,
): IDayAvailability[] => {
  const byDay: Record<number, IDayAvailability> = {};
  (timeTable.dailyTimeTables || []).forEach((entry) => {
    byDay[entry.day] = {
      active: !!entry.active,
      startTime:
        entry.beginAt != null
          ? minutesToTime(entry.beginAt)
          : entry.startTime || "",
      endTime:
        entry.endAt != null ? minutesToTime(entry.endAt) : entry.endTime || "",
    };
  });
  return Array.from(
    { length: 7 },
    (_, day) => byDay[day] || { active: false, startTime: "", endTime: "" },
  );
};

export const getTimeTables = (userId: string): Promise<IWizardTimeTable[]> =>
  schedulingFetchGet(`coach_profiles/${userId}/time_tables`, {}).then(
    (timeTables: IWizardTimeTable[]) => timeTables || [],
  );

export const createTimeTable = (
  userId: string,
  days: IDayAvailability[],
  lessonRateIds: Array<string | number>,
): Promise<unknown> =>
  schedulingFetchPost(`coach_profiles/${userId}/time_tables`, {
    time_table: {
      id: null,
      name: WIZARD_SCHEDULE_NAME,
      dailyTimeTables: buildDailyTimeTables(days),
      customPeriod: false,
      startDate: null,
      endDate: null,
      lessonRateIds,
    },
  });

// bulk_update only applies times to daily rows it can identify: /calendar/
// availability sends each row's id (parseSchedules spreads the fetched rows),
// and an id-less entry gets its active flag applied but keeps its old
// begin_at/end_at. Merging onto the fetched rows carries the ids along.
export const mergeDailyTimeTables = (
  existing: IWizardTimeTable,
  days: IDayAvailability[],
): IWizardTimeTable["dailyTimeTables"] =>
  buildDailyTimeTables(days).map((entry) => {
    const current = (existing.dailyTimeTables || []).find(
      (row) => row.day === entry.day,
    );
    return { ...current, ...entry };
  });

export const updateTimeTable = (
  userId: string,
  existing: IWizardTimeTable,
  days: IDayAvailability[],
  lessonRateIds: Array<string | number>,
): Promise<unknown> =>
  schedulingFetchPatch(`coach_profiles/${userId}/time_tables/bulk_update`, {
    time_tables: [
      {
        ...existing,
        dailyTimeTables: mergeDailyTimeTables(existing, days),
        customPeriod: false,
        startDate: null,
        endDate: null,
        lessonRateIds,
      },
    ],
  });

// All non-destroyed rate ids, for linking the wizard schedule to every booking
// option — mirroring how /calendar/pricing defaults new rates to all schedules.
export const getLessonRateIds = (
  userId: string,
): Promise<Array<string | number>> =>
  getPricingGroup(userId).then((group) =>
    (group?.lessonRates || [])
      .filter((rate) => !rate._destroy && rate.id != null)
      .map((rate) => rate.id),
  );
